/**
 * Staff file notes and warnings (2026-10-19). Server: HHNodeServer
 * `staffRecordService`, `/api/staff-records`, migration 20261019-90.
 */

export type NoteCategory =
  | 'general' | 'conversation' | 'performance' | 'conduct' | 'resignation'
  | 'termination' | 'health' | 'commendation' | 'other';

export const NOTE_CATEGORY_LABEL: Record<NoteCategory, string> = {
  general: 'General',
  conversation: 'Conversation or meeting',
  performance: 'Performance',
  conduct: 'Conduct',
  resignation: 'Resignation',
  termination: 'Termination',
  health: 'Health and wellbeing',
  commendation: 'Commendation',
  other: 'Other',
};

export const NOTE_CATEGORIES = Object.keys(NOTE_CATEGORY_LABEL) as NoteCategory[];

export interface StaffNote {
  id: number;
  user_id: number;
  category: NoteCategory;
  /** YYYY-MM-DD */
  note_date: string;
  title: string | null;
  body: string;
  /** Pinned at the top of the file as important information. */
  important: boolean;
  created_by: number | null;
  created_by_name: string | null;
  created_at: string;
  updated_by: number | null;
  updated_by_name: string | null;
  updated_at: string | null;
  removed_at: string | null;
  removed_by_name: string | null;
  removed_reason: string | null;
  /** How many earlier versions are kept. */
  revisions: number;
}

export interface StaffNoteRevision {
  id: number;
  category: NoteCategory;
  note_date: string;
  title: string | null;
  body: string;
  important: boolean;
  written_by_name: string | null;
  written_at: string;
  replaced_by_name: string | null;
  replaced_at: string;
}

export interface StaffNoteInput {
  category: NoteCategory;
  note_date?: string | null;
  title?: string | null;
  body: string;
  important?: boolean;
}

export type WarningLevel = 'verbal' | 'written' | 'final';

export const WARNING_LEVEL_LABEL: Record<WarningLevel, string> = {
  verbal: 'Verbal warning',
  written: 'Written warning',
  final: 'Final written warning',
};

export type WarningStatus = 'draft' | 'issued' | 'acknowledged' | 'withdrawn';

export interface StaffWarning {
  id: number;
  user_id: number;
  level: WarningLevel;
  incident_date: string | null;
  subject: string;
  details: string;
  expectations: string | null;
  consequences: string | null;
  support: string | null;
  status: WarningStatus;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
  issued_by_name: string | null;
  issued_at: string | null;
  /** The words as issued: what the employee reads and acknowledges. */
  issued_text: string | null;
  issued_hash: string | null;
  sent_in_app: boolean;
  given_in_person: boolean;
  first_read_at: string | null;
  reminded_at: string | null;
  acknowledged_at: string | null;
  acknowledged_name: string | null;
  acknowledged_method: 'in_app' | 'in_person' | null;
  acknowledgement_recorded_by_name: string | null;
  employee_response: string | null;
  withdrawn_at: string | null;
  withdrawn_by_name: string | null;
  withdrawn_reason: string | null;
}

export interface StaffWarningInput {
  level: WarningLevel;
  incident_date?: string | null;
  subject: string;
  details: string;
  expectations?: string | null;
  consequences?: string | null;
  support?: string | null;
}

export interface StaffRecordFile {
  id: number;
  note_id: number | null;
  warning_id: number | null;
  file_name: string;
  mime_type: string | null;
  size_bytes: number | null;
  uploaded_by_name: string | null;
  created_at: string;
}

export interface StaffFile {
  person: { id: number; name: string; active_here: boolean };
  notes: StaffNote[];
  warnings: StaffWarning[];
  files: StaffRecordFile[];
}

/** A warning as the employee sees it. */
export interface MyWarning {
  id: number;
  level: WarningLevel;
  subject: string;
  status: Exclude<WarningStatus, 'draft'>;
  issued_at: string;
  issued_text: string;
  issued_hash: string;
  issued_by_name: string | null;
  first_read_at: string | null;
  acknowledged_at: string | null;
  acknowledged_name: string | null;
  acknowledged_method: 'in_app' | 'in_person' | null;
  employee_response: string | null;
  withdrawn_at: string | null;
  withdrawn_reason: string | null;
}
